/**
 * /status — service status page.
 *
 * Renders the static /status.json summary (the same document agents fetch) so a
 * human visiting /status sees real component health instead of a 404 or the
 * homepage. The JSON is the source of truth; this page is a thin presenter.
 * For real-time liveness, GET https://api.inboxguard.io/health.
 */
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, AlertTriangle, XCircle, HelpCircle } from 'lucide-react';
import { PublicHeader } from '@/components/PublicHeader';
import { PublicFooter } from '@/components/PublicFooter';

interface StatusComponent {
  name: string;
  status: string;
}
interface StatusDoc {
  status: { indicator: string; description: string };
  components: StatusComponent[];
  support?: string;
}

function statusVisual(status: string) {
  switch (status) {
    case 'operational':
      return { Icon: CheckCircle2, cls: 'text-success', label: 'Operational' };
    case 'degraded_performance':
    case 'partial_outage':
      return { Icon: AlertTriangle, cls: 'text-warning', label: 'Degraded' };
    case 'major_outage':
      return { Icon: XCircle, cls: 'text-destructive', label: 'Outage' };
    default:
      return { Icon: HelpCircle, cls: 'text-muted-foreground', label: status };
  }
}

export function StatusPage() {
  const [doc, setDoc] = useState<StatusDoc | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    fetch('/status.json', { headers: { accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => active && setDoc(d as StatusDoc))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  const overall = doc ? statusVisual(doc.status.indicator) : null;

  return (
    <>
      <Helmet>
        <title>Status — InboxGuard</title>
        <meta
          name="description"
          content="InboxGuard service status: real-time health of the API, MCP server, domain scanning, DMARC ingest, dashboard, and alerting. Machine-readable at /status.json."
        />
        <link rel="canonical" href="https://inboxguard.io/status" />
        <meta name="robots" content="index,follow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <PublicHeader />

        <section className="container mx-auto max-w-3xl px-4 py-16">
          <h1 className="text-3xl font-bold tracking-tight">Service status</h1>

          {error && (
            <p className="mt-6 text-muted-foreground">
              Could not load the status summary. Check{' '}
              <a className="text-primary hover:underline" href="/status.json">
                /status.json
              </a>{' '}
              or <code>GET https://api.inboxguard.io/health</code> directly.
            </p>
          )}

          {overall && doc && (
            <>
              <div className="mt-6 flex items-center gap-3 rounded-lg border border-border bg-card p-5">
                <overall.Icon className={`h-7 w-7 ${overall.cls}`} aria-hidden />
                <div>
                  <div className="text-lg font-semibold">{doc.status.description}</div>
                  <div className="text-sm text-muted-foreground">
                    Live liveness: <code>GET https://api.inboxguard.io/health</code> ·
                    machine-readable: <a className="text-primary hover:underline" href="/status.json">/status.json</a>
                  </div>
                </div>
              </div>

              <ul className="mt-8 divide-y divide-border rounded-lg border border-border">
                {doc.components.map((c) => {
                  const v = statusVisual(c.status);
                  return (
                    <li key={c.name} className="flex items-center justify-between px-5 py-3.5">
                      <span className="text-sm">{c.name}</span>
                      <span className={`flex items-center gap-1.5 text-sm font-medium ${v.cls}`}>
                        <v.Icon className="h-4 w-4" aria-hidden />
                        {v.label}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div className="mt-8 rounded-lg border border-border bg-muted/30 p-5 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">Agents: handling degradation</p>
                <p className="mt-2">
                  On <code>429</code> honor <code>Retry-After</code>. On <code>503</code>/
                  <code>5xx</code>, retry idempotent reads and <code>POST /scan-domain</code> with
                  exponential backoff + jitter (1s → 2s → 4s → 8s, cap ~30s, ~5 tries). Don&apos;t
                  blindly retry writes — use the <code>Idempotency-Key</code> header. Full guidance:{' '}
                  <a className="text-primary hover:underline" href="/auth.md">/auth.md</a>.
                </p>
              </div>

              <p className="mt-8 text-sm text-muted-foreground">
                Incidents &amp; support: <a className="text-primary hover:underline" href="mailto:support@inboxguard.io">support@inboxguard.io</a>
              </p>
            </>
          )}

          {!doc && !error && <p className="mt-6 text-muted-foreground">Loading status…</p>}
        </section>

        <PublicFooter />
      </div>
    </>
  );
}
